import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, Button, Card, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pluralize from 'pluralize';
import { useEffectOnce } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';

import { tGender } from '@models/User';
import { formatDate } from '@utils/datetime';
import { AmountWithDiscount } from '@components/Base';
import { InsurancePolicyUser, InsurancePolicyUserList } from '@models/InsurancePolicyUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencil } from '@fortawesome/free-solid-svg-icons';

import { InsurancePolicyUserFormModal, InsurancePolicyUserFormValues } from './insurance-policy-user-form-modal';
import { selectFeatureInsurancePolicyUser } from './selector';
import {
  insurancePolicyUserCreate,
  InsurancePolicyUserCreatePayload,
  insurancePolicyUsersFetch,
  InsurancePolicyUsersFetchPayload,
  insurancePolicyUserUpdate,
  InsurancePolicyUserUpdatePayload,
  stateClear,
} from './action';
import { InsurancePolicy } from '@models/InsurancePolicy';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<InsurancePolicy, 'id' | 'activationDate' | 'activationPeriod' | 'benefitTemplates'>;

const InsurancePolicyUserCardBody = ({ id: insurancePolicyId, activationDate, activationPeriod, benefitTemplates }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { policyUsers, isLoading, pagination } = useSelector(selectFeatureInsurancePolicyUser);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [selectedPolicyUser, setSelectedPolicyUser] = useState<InsurancePolicyUser | null>(null);

  const columns = useMemo(() => [
    {
      cell: (user) => (
        <Button onClick={() => handleEdit(user)} variant="outline-primary">
          <FontAwesomeIcon icon={faPencil} />
        </Button>
      )
      ,
      width: '44px',
      button: 'true',
      center: 'true',
    },
    {
      name: t('user.name'),
      selector: ({ name }) => name,
      width: '120px',
    },
    {
      name: t('user.dob'),
      selector: ({ dob }) => formatDate(dob),
      width: '120px',
    },
    {
      name: t('user.gender'),
      selector: ({ gender }) => tGender(gender)
    },
    {
      name: t('common:startDate'),
      selector: ({ startedAt }) => formatDate(startedAt),
      width: '120px',
    },
    {
      name: t('common:endDate'),
      selector: ({ endedAt }) => formatDate(endedAt),
      width: '120px',
    },
    {
      name: t('common:plan'),
      selector: ({ benefitTemplate }) => benefitTemplate.name,
      width: '120px',
    },
    {
      name: t('common:premium'),
      selector: ({ premium }) => <AmountWithDiscount skipIncluded finalAmount={premium} />
    },
    {
      name: t('common:refundedPremium'),
      selector: ({ refundedPremium }) => <AmountWithDiscount skipIncluded finalAmount={refundedPremium} />
    },
  ] as TableColumn<InsurancePolicyUserList>[], []);

  const handleEdit = (policyUser: InsurancePolicyUser) => {
    setSelectedPolicyUser(policyUser);
    setShowFormDialog(true);
  };

  useEffect(() => {
    const payload: InsurancePolicyUsersFetchPayload = { insurancePolicyId, page, perPage };
    dispatch(insurancePolicyUsersFetch(payload));
  }, [insurancePolicyId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              {pluralize(t('common:user'), 2)} ({pagination?.total})
            </Toggle>
            <Button onClick={() => setShowFormDialog(true)} variant="outline-primary">
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              {t('common:add')}
            </Button>
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <DataTable
              columns={columns}
              data={policyUsers ?? []}
              customStyles={customStyles}
              fixedHeader
              pagination
              paginationServer
              progressPending={isLoading}
              paginationPerPage={perPage}
              paginationTotalRows={pagination?.total ?? 0}
              onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
              onChangePage={(newPage) => setPage(newPage)}
            />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <InsurancePolicyUserFormModal
        benefitTemplates={benefitTemplates}
        activationDate={activationDate}
        activationPeriod={activationPeriod}
        policyUser={selectedPolicyUser}
        show={showFormDialog}
        onHide={() => setShowFormDialog(false)}
        onSubmit={(values: InsurancePolicyUserFormValues) => {
          if (selectedPolicyUser) {
            const payload: InsurancePolicyUserUpdatePayload = { id: selectedPolicyUser.id, ...values };
            dispatch(insurancePolicyUserUpdate(payload));
          } else {
            const payload: InsurancePolicyUserCreatePayload = { insurancePolicyId, ...values };
            dispatch(insurancePolicyUserCreate(payload));
          }
          setShowFormDialog(false);
        }}
      />
    </>
  );
};

export { InsurancePolicyUserCardBody };
